// src/modules/Home.jsx
import { useState, useEffect, useMemo } from "react";
import { C } from "../constants";
import { Ic } from "../components/ui/Icons";
import { StatCard, BrandTable, Badge } from "../components/ui/Components";
import { apiCall } from "../api";

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return mins <= 1 ? "just now" : `${mins} minutes ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs === 1 ? "1 hour ago" : `${hrs} hours ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  return `${Math.floor(days / 30)} month${Math.floor(days / 30) > 1 ? "s" : ""} ago`;
}

const ORDER_ACTIVE_STATUSES = ["Approved", "Paused", "Processed", "Delivered"];

export default function Home({ user, go }) {
  const [requests, setRequests] = useState([]);
  const [quotes, setQuotes]     = useState([]);
  const [orders, setOrders]     = useState([]);
  const [loading, setLoading]   = useState(true);

  const h = new Date().getHours();
  const greeting = h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
  const fmt = n => "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [rRes, qRes, oRes] = await Promise.all([
          apiCall("getRequests", { email: user.email, company: user.company, role: user.role || "CLIENT" }),
          apiCall("getQuotes",   { email: user.email, company: user.company, role: user.role || "CLIENT" }),
          apiCall("getOrders",   { email: user.email, company: user.company, role: user.role || "CLIENT" }),
        ]);
        if (rRes.success) setRequests(rRes.requests || []);
        if (qRes.success) setQuotes(qRes.quotes || []);
        if (oRes.success) setOrders(oRes.orders || []);
      } catch (err) { console.error("Home data load failed:", err); }
      setLoading(false);
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Stat calculations ───────────────────────────────────────
  const pendingRequests = useMemo(() =>
    requests.filter(r => r.status === "pending" || r.status === "review").length,
  [requests]);

  const openQuotes = useMemo(() =>
    quotes.filter(q => (q.status || "Open") === "Open"),
  [quotes]);

  const activeOrders = useMemo(() =>
    orders.filter(o => ORDER_ACTIVE_STATUSES.includes(o.status)),
  [orders]);

  const openQuotesTotal = useMemo(() =>
    openQuotes.reduce((sum, q) => sum + (parseFloat(q.amount) || 0), 0),
  [openQuotes]);

  const ordersTotal = useMemo(() =>
    activeOrders.reduce((sum, o) => sum + (parseFloat(o.amount) || 0), 0),
  [activeOrders]);

  // Open quotes by manufacturer — sorted descending
  const quotesByBrand = useMemo(() => {
    const map = {};
    openQuotes.forEach(q => {
      const brand = q.manufacturer || "Other";
      map[brand] = (map[brand] || 0) + (parseFloat(q.amount) || 0);
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .reduce((obj, [k, v]) => { obj[k] = v; return obj; }, {});
  }, [openQuotes]);

  // Orders by manufacturer — sorted descending
  const ordersByBrand = useMemo(() => {
    const map = {};
    activeOrders.forEach(o => {
      const brand = o.manufacturer || "Other";
      map[brand] = (map[brand] || 0) + (parseFloat(o.amount) || 0);
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .reduce((obj, [k, v]) => { obj[k] = v; return obj; }, {});
  }, [activeOrders]);

  const quoteBrandNames = Object.keys(quotesByBrand);
  const orderBrandNames = Object.keys(ordersByBrand);

  // ─── Recent Activity (last 30 days) ──────────────────────────
  const recentActivity = useMemo(() => {
    const items = [];
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;

    requests.forEach(r => {
      const submittedTs = new Date(r.date).getTime();
      const updatedTs   = new Date(r.lastUpdated).getTime();
      if (submittedTs >= cutoff) {
        items.push({ id: `req-new-${r.id}`, type: "project", targetId: r.id,
          text: `New request ${r.id} submitted`, subtext: r.type,
          time: r.date, status: r.status || "pending", ts: submittedTs });
      } else if (updatedTs >= cutoff && r.status !== "pending" && updatedTs !== submittedTs) {
        const label = { review: "In Review", quoted: "Quoted", rejected: "Rejected" }[r.status] || r.status;
        items.push({ id: `req-upd-${r.id}`, type: "project", targetId: r.id,
          text: `Request ${r.id} marked as ${label}`, subtext: r.type,
          time: r.lastUpdated, status: r.status || "pending", ts: updatedTs });
      }
    });

    quotes.forEach(q => {
      const createdTs = new Date(q.quotedDate).getTime();
      const updatedTs = new Date(q.lastUpdated).getTime();
      if (createdTs >= cutoff) {
        items.push({ id: `qt-new-${q.id}`, type: "quote", targetId: q.id,
          text: `Quote ${q.quoteNumber} created`, subtext: q.company || q.manufacturer,
          time: q.quotedDate, status: "quoted", ts: createdTs });
      } else if (updatedTs >= cutoff && updatedTs !== createdTs) {
        items.push({ id: `qt-upd-${q.id}`, type: "quote", targetId: q.id,
          text: `Quote ${q.quoteNumber} → ${q.status || "Open"}`,
          subtext: q.company || q.manufacturer, time: q.lastUpdated,
          status: q.status === "Ordered" ? "approved" : q.status === "Closed - Lost" ? "rejected" : "quoted",
          ts: updatedTs });
      }
    });

    orders.forEach(o => {
      const submittedTs = new Date(o.submittedDate).getTime();
      const updatedTs   = new Date(o.lastUpdated).getTime();
      if (submittedTs >= cutoff) {
        items.push({ id: `ord-new-${o.id}`, type: "order", targetId: o.id,
          text: `Order placed for ${o.quoteNumber}`, subtext: o.company || o.manufacturer,
          time: o.submittedDate, status: "pending", ts: submittedTs });
      } else if (updatedTs >= cutoff && updatedTs !== submittedTs) {
        items.push({ id: `ord-upd-${o.id}`, type: "order", targetId: o.id,
          text: `Order ${o.quoteNumber} → ${o.status}`,
          subtext: o.company || o.manufacturer, time: o.lastUpdated,
          status: o.status === "Delivered" ? "approved" : o.status === "Rejected" ? "rejected" : "quoted",
          ts: updatedTs });
      }
    });

    return items.sort((a, b) => b.ts - a.ts).slice(0, 15);
  }, [requests, quotes, orders]);

  const handleActivityClick = (item) => {
    if (!go) return;
    if (item.type === "project") go("projects");
    else if (item.type === "quote") go("quotes");
    else if (item.type === "order") go("orders");
  };

  const dotColor = (type) => {
    if (type === "quote") return C.warn;
    if (type === "order") return C.ok;
    return C.info;
  };

  return (
    <div style={{ maxWidth: 960 }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 26, fontWeight: 700, color: C.t1, margin: "0 0 4px" }}>{greeting}, {user.name.split(" ")[0]}</h1>
        <p style={{ fontSize: 14, color: C.t2, margin: 0 }}>Here's what's happening with your projects and quotes.</p>
      </div>

      {/* Top stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16, marginBottom: 16 }}>
        <StatCard label="Pending Requests" value={loading ? "—" : pendingRequests} icon={Ic.Project} color={C.info} />
        <StatCard label="Open Quotes" value={loading ? "—" : openQuotes.length} icon={Ic.Quote} color={C.warn} />
        <StatCard label="Orders" value={loading ? "—" : activeOrders.length} icon={Ic.Order} color={C.ok} />
      </div>

      {/* Financial cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 16, marginBottom: 16 }}>
        <StatCard label="Open Quotes Total" value={loading ? "—" : fmt(openQuotesTotal)} icon={Ic.Dollar} color={C.purple} large />
        <StatCard label="Orders Total" value={loading ? "—" : fmt(ordersTotal)} icon={Ic.Dollar} color={C.navy} large />
      </div>

      {/* Brand tables */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 16, marginBottom: 32 }}>
        {loading ? (
          <div style={{ background: C.white, borderRadius: 14, border: `1px solid ${C.border}`, padding: "32px 24px", textAlign: "center", color: C.t3, fontSize: 13 }}>
            Loading quotes by brand...
          </div>
        ) : quoteBrandNames.length > 0 ? (
          <BrandTable title="Open Quotes by Brand" data={quotesByBrand} brands={quoteBrandNames} color={C.purple} />
        ) : (
          <div style={{ background: C.white, borderRadius: 14, border: `1px solid ${C.border}`, padding: "32px 24px", textAlign: "center", color: C.t3, fontSize: 13 }}>
            No open quotes to display.
          </div>
        )}
        {loading ? (
          <div style={{ background: C.white, borderRadius: 14, border: `1px solid ${C.border}`, padding: "32px 24px", textAlign: "center", color: C.t3, fontSize: 13 }}>
            Loading orders by brand...
          </div>
        ) : orderBrandNames.length > 0 ? (
          <BrandTable title="Orders by Brand" data={ordersByBrand} brands={orderBrandNames} color={C.navy} />
        ) : (
          <div style={{ background: C.white, borderRadius: 14, border: `1px solid ${C.border}`, padding: "32px 24px", textAlign: "center", color: C.t3, fontSize: 13 }}>
            No active orders to display.
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div style={{ background: C.white, borderRadius: 14, border: `1px solid ${C.border}`, overflow: "hidden" }}>
        <div style={{ padding: "18px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: C.t1, margin: 0 }}>Recent Activity</h2>
          <span style={{ fontSize: 12, color: C.t3 }}>Last 30 days · click to open</span>
        </div>
        <div style={{ maxHeight: 320, overflowY: "auto" }}>
          {loading ? (
            <div style={{ padding: "40px 24px", textAlign: "center", color: C.t3, fontSize: 13 }}>Loading activity...</div>
          ) : recentActivity.length === 0 ? (
            <div style={{ padding: "40px 24px", textAlign: "center", color: C.t3, fontSize: 13 }}>No activity in the last 30 days.</div>
          ) : recentActivity.map((a, i) => (
            <div key={a.id}
              onClick={() => handleActivityClick(a)}
              style={{ padding: "13px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: i < recentActivity.length - 1 ? `1px solid ${C.border}` : "none", transition: "background 0.15s", cursor: go ? "pointer" : "default" }}
              onMouseEnter={e => e.currentTarget.style.background = C.grayBg}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, background: dotColor(a.type) }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: C.t1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.text}</div>
                  {a.subtext && <div style={{ fontSize: 11, color: C.t3, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.subtext}</div>}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0, marginLeft: 12 }}>
                <Badge status={a.status} />
                <span style={{ fontSize: 11, color: C.t3, whiteSpace: "nowrap" }}>{timeAgo(a.time)}</span>
                {go && <span style={{ fontSize: 11, color: C.t3 }}>→</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
